import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../../supabaseClient';
import { Task, PhysicalUnit, Level, ProjectInfo, ProtocolState, WeeklyPlan, GraphicPlan, GraphicPlanRegion, Responsible } from '../../types';
import { isMatrixValueApplies, isTaskUnitAssigned } from '../../utils/planningUtils';
import { PdfKonvaViewer, PdfRegion, PdfRegionStripe, PdfRegionMarker, PdfKonvaViewerHandle } from './PdfKonvaViewer';

export interface GraphicProgressPlanningViewProps {
    activeProjectId: string;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, any>;
    protocolConfigs: any;
    projectInfo: ProjectInfo;
    savedProtocols: Record<string, ProtocolState>;
    weeklyPlans: WeeklyPlan[];
    responsibles?: Responsible[];
    activePlanId?: number | null;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    onClose?: () => void;
}

// Paleta dinámica de colores para asignación automática a tareas en la semana
const COLOR_PALETTE = [
    '#3B82F6', // Azul vibrante
    '#10B981', // Esmeralda
    '#F59E0B', // Ámbar / Naranja
    '#8B5CF6', // Violeta
    '#EC4899', // Rosa
    '#06B6D4', // Cian
    '#84CC16', // Lima
    '#F97316', // Naranja oscuro
    '#6366F1', // Índigo
    '#14B8A6', // Teal
    '#D946EF', // Fucsia
    '#E11D48', // Carmesí
];

const SHAPE_PALETTE: ('circle' | 'square' | 'triangle' | 'diamond' | 'star')[] = [
    'circle',
    'square',
    'triangle',
    'diamond'
];

/**
 * Convierte nombres de nivel estándar en prefijos compactos y profesionales:
 * Ejemplos:
 *  - "Manzana 1" / "Manzana A" -> "Mz 1" / "Mz A"
 *  - "Piso 1" / "Piso 2" -> "P1" / "P2"
 *  - "Subterráneo 1" / "Subterraneo -1" -> "Sub 1" / "Sub -1"
 *  - "Edificio A" / "Edificio 1" -> "Ed A" / "Ed 1"
 *  - "Zona 1" / "Zona A" -> "Z1" / "ZA"
 *  - "Sector 2" -> "Sec 2"
 *  - "Torre 1" -> "T1"
 */
export const formatLevelPrefix = (rawName?: string): string => {
    if (!rawName) return '';
    const trimmed = rawName.trim();

    // Reemplazos de prefijo con regex insensible a mayúsculas
    if (/^manzana\s*/i.test(trimmed)) {
        return trimmed.replace(/^manzana\s*/i, 'Mz ');
    }
    if (/^piso\s*/i.test(trimmed)) {
        return trimmed.replace(/^piso\s*/i, 'P');
    }
    if (/^subterr[aá]neo\s*/i.test(trimmed)) {
        return trimmed.replace(/^subterr[aá]neo\s*/i, 'Sub ');
    }
    if (/^edificio\s*/i.test(trimmed)) {
        return trimmed.replace(/^edificio\s*/i, 'Ed ');
    }
    if (/^zona\s*/i.test(trimmed)) {
        return trimmed.replace(/^zona\s*/i, 'Z');
    }
    if (/^sector\s*/i.test(trimmed)) {
        return trimmed.replace(/^sector\s*/i, 'Sec ');
    }
    if (/^torre\s*/i.test(trimmed)) {
        return trimmed.replace(/^torre\s*/i, 'T');
    }
    if (/^etapa\s*/i.test(trimmed)) {
        return trimmed.replace(/^etapa\s*/i, 'Et ');
    }

    // Si ya viene formateado corto o no tiene regla especial, devolver limpio
    return trimmed;
};

/**
 * Genera la etiqueta visible para un recinto combinando el prefijo de nivel y el nombre de la unidad física
 * Ej: "Mz 1 - Casa 12", "P3 - Depto 302", "Ed A - Bodega 01"
 */
export const formatPhysicalUnitLabel = (unitName?: string, levelName?: string, fallbackLabel?: string): string => {
    if (!unitName) return fallbackLabel || '';
    const lvlPrefix = formatLevelPrefix(levelName);
    if (!lvlPrefix) return unitName;
    return `${lvlPrefix} - ${unitName}`;
};

export const GraphicProgressPlanningView: React.FC<GraphicProgressPlanningViewProps> = ({
    activeProjectId,
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    projectInfo,
    savedProtocols,
    weeklyPlans,
    responsibles = [],
    activePlanId,
    isFullscreen: isFullscreenProp,
    onToggleFullscreen,
    onClose
}) => {
    const [plans, setPlans] = useState<GraphicPlan[]>([]);
    const [regions, setRegions] = useState<GraphicPlanRegion[]>([]);
    const [loading, setLoading] = useState(true);

    const [selectedLevelId, setSelectedLevelId] = useState<string>('all');
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');

    // Estado para Pantalla Completa y Exportación
    const [internalFullscreen, setInternalFullscreen] = useState(false);
    const isFullscreen = isFullscreenProp !== undefined ? isFullscreenProp : internalFullscreen;
    const handleToggleFullscreen = () => {
        if (onToggleFullscreen) {
            onToggleFullscreen();
        } else {
            setInternalFullscreen(prev => !prev);
        }
    };
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const konvaViewerRef = useRef<PdfKonvaViewerHandle>(null);

    // Estado para el Popover interactivo
    const [popoverInfo, setPopoverInfo] = useState<{
        unit: PhysicalUnit;
        level?: Level;
        x: number;
        y: number;
        tasks: {
            task: Task;
            subcontractorName: string;
            percentComprometido?: number;
            percentCumplimiento?: number;
            executed?: boolean | null;
            statusText: string;
            statusColor: string;
        }[];
    } | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    // Fallbacks defensivos
    const safeUnits = units || [];
    const safeLevels = levels || [];
    const safeTasks = tasks || [];
    const safeSavedProtocols = savedProtocols || {};
    const safeProtocolConfigs = protocolConfigs || {};
    const safeMatrixData = matrixData || {};

    const loadData = async () => {
        setLoading(true);
        try {
            const [plansRes, regionsRes] = await Promise.all([
                supabase.from('graphic_plans').select('*').eq('project_id', activeProjectId),
                supabase.from('graphic_plan_regions').select('*').eq('project_id', activeProjectId)
            ]);

            if (plansRes.data) {
                setPlans(plansRes.data);
                if (plansRes.data.length > 0 && !selectedPlanId) {
                    setSelectedPlanId(plansRes.data[0].id);
                }
            } else {
                setPlans([]);
            }

            if (regionsRes.data) {
                setRegions(regionsRes.data);
            } else {
                setRegions([]);
            }
        } catch (err) {
            console.error("Error cargando planos para avance gráfico de planificación", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeProjectId) loadData();
    }, [activeProjectId]);

    // Semana activa
    const currentActivePlan = useMemo(() => {
        if (activePlanId !== undefined && activePlanId !== null) {
            return weeklyPlans.find(p => p.id === activePlanId);
        }
        return weeklyPlans.find(p => p.status !== 'Cerrada') || weeklyPlans[weeklyPlans.length - 1];
    }, [weeklyPlans, activePlanId]);

    // Aplanar tareas
    const flatTasks = useMemo(() => {
        const list: Task[] = [];
        const traverse = (arr: Task[]) => {
            arr.forEach(t => {
                if (t.type === 'task') list.push(t);
                if (t.children && t.children.length > 0) traverse(t.children);
            });
        };
        traverse(safeTasks);
        return list;
    }, [safeTasks]);

    // Unidades filtradas por nivel
    const filteredUnits = useMemo(() => {
        if (selectedLevelId === 'all') return safeUnits;
        return safeUnits.filter(u => u.levelId === selectedLevelId);
    }, [safeUnits, selectedLevelId]);

    // Auto-seleccionar plano correspondiente al nivel si existe
    useEffect(() => {
        if (selectedLevelId !== 'all') {
            const regionOfLevel = regions.find(r => r.level_id === selectedLevelId);
            if (regionOfLevel) {
                setSelectedPlanId(regionOfLevel.plan_id);
            }
        }
    }, [selectedLevelId, regions]);

    // Tareas programadas en la semana activa para el conjunto de unidades filtradas
    const weeklyProgrammedTasks = useMemo(() => {
        if (!currentActivePlan || !currentActivePlan.allocations) return [];

        const taskMap = new Map<string, {
            task: Task;
            color: string;
            isSurface: boolean;
            shape: 'circle' | 'square' | 'triangle' | 'diamond' | 'star';
            unitIds: Set<string>;
        }>();

        const unitIdSet = new Set(filteredUnits.map(u => u.id));
        let colorIdx = 0;
        let shapeIdx = 0;

        currentActivePlan.allocations.forEach(alloc => {
            if (unitIdSet.has(alloc.unitId)) {
                const foundTask = flatTasks.find(t => t.id === alloc.taskId);
                if (foundTask) {
                    if (!taskMap.has(foundTask.id)) {
                        const unitNorm = (foundTask.unit || '').toLowerCase().trim();
                        // Superficie si la unidad es m2 o contiene m2/sup/superficie
                        const isSurface = unitNorm.includes('m2') || unitNorm.includes('m²') || unitNorm.includes('sup');
                        const color = COLOR_PALETTE[colorIdx % COLOR_PALETTE.length];
                        const shape = SHAPE_PALETTE[shapeIdx % SHAPE_PALETTE.length];
                        colorIdx++;
                        if (!isSurface) shapeIdx++;

                        taskMap.set(foundTask.id, {
                            task: foundTask,
                            color,
                            isSurface,
                            shape,
                            unitIds: new Set([alloc.unitId])
                        });
                    } else {
                        taskMap.get(foundTask.id)!.unitIds.add(alloc.unitId);
                    }
                }
            }
        });

        return Array.from(taskMap.values());
    }, [currentActivePlan, filteredUnits, flatTasks]);

    // Plano activo
    const activePlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);
    
    // Regiones del plano activo
    const activePlanRegions = useMemo(() => {
        return regions.filter(r => r.plan_id === selectedPlanId);
    }, [regions, selectedPlanId]);

    // Construir mapa de asignaciones por unidad para la semana
    const unitAllocationsMap = useMemo(() => {
        const map = new Map<string, typeof currentActivePlan.allocations>();
        if (!currentActivePlan || !currentActivePlan.allocations) return map;

        currentActivePlan.allocations.forEach(alloc => {
            if (!map.has(alloc.unitId)) {
                map.set(alloc.unitId, []);
            }
            map.get(alloc.unitId)!.push(alloc);
        });
        return map;
    }, [currentActivePlan]);

    // Mapeo a PdfRegion para Konva con reglas de Superficie vs Puntual
    const konvaRegions: PdfRegion[] = useMemo(() => {
        return activePlanRegions.map(reg => {
            const unit = safeUnits.find(u => u?.id === reg.physical_unit_id);
            const level = safeLevels.find(l => l?.id === reg.level_id);
            const shortLabel = formatPhysicalUnitLabel(unit?.name, level?.name, reg.label || '');

            if (!reg.physical_unit_id) {
                return {
                    id: reg.id,
                    pageNumber: reg.page_number || 1,
                    xPercent: reg.x_percent || 0,
                    yPercent: reg.y_percent || 0,
                    widthPercent: reg.width_percent || 10,
                    heightPercent: reg.height_percent || 10,
                    label: shortLabel,
                    color: 'rgba(203, 213, 225, 0.4)',
                    physicalUnitId: undefined
                };
            }

            const unitAllocs = unitAllocationsMap.get(reg.physical_unit_id) || [];
            
            // Separar tareas de superficie de las puntuales
            const surfaceStripes: PdfRegionStripe[] = [];
            const pointMarkers: PdfRegionMarker[] = [];

            unitAllocs.forEach(alloc => {
                const progTaskInfo = weeklyProgrammedTasks.find(pt => pt.task.id === alloc.taskId);
                if (progTaskInfo) {
                    if (progTaskInfo.isSurface) {
                        surfaceStripes.push({
                            color: progTaskInfo.color,
                            label: progTaskInfo.task.name,
                            weight: 1,
                            taskId: progTaskInfo.task.id
                        });
                    } else {
                        pointMarkers.push({
                            id: `${reg.id}-${progTaskInfo.task.id}`,
                            shape: progTaskInfo.shape,
                            color: progTaskInfo.color,
                            label: progTaskInfo.task.name,
                            taskId: progTaskInfo.task.id
                        });
                    }
                }
            });

            // Si no tiene tareas de superficie programadas pero sí tiene puntuales o ninguna
            const defaultBgColor = surfaceStripes.length === 0 
                ? (unitAllocs.length > 0 ? 'rgba(241, 245, 249, 0.6)' : 'rgba(226, 232, 240, 0.3)')
                : undefined;

            return {
                id: reg.id,
                pageNumber: reg.page_number || 1,
                xPercent: reg.x_percent || 0,
                yPercent: reg.y_percent || 0,
                widthPercent: reg.width_percent || 10,
                heightPercent: reg.height_percent || 10,
                label: shortLabel,
                levelName: formatLevelPrefix(level?.name),
                unitName: unit?.name,
                color: defaultBgColor,
                physicalUnitId: reg.physical_unit_id,
                stripes: surfaceStripes.length > 0 ? surfaceStripes : undefined,
                markers: pointMarkers.length > 0 ? pointMarkers : undefined
            };
        });
    }, [activePlanRegions, safeUnits, safeLevels, unitAllocationsMap, weeklyProgrammedTasks]);

    // Manejador de clic e interacción para Popover
    const handleRegionSelect = (reg: PdfRegion | null, mouseEvent?: any) => {
        if (!reg || !reg.physicalUnitId) {
            setPopoverInfo(null);
            return;
        }

        const unit = safeUnits.find(u => u.id === reg.physicalUnitId);
        if (!unit) return;

        const level = safeLevels.find(l => l.id === unit.levelId);
        const unitAllocs = unitAllocationsMap.get(unit.id) || [];

        const tasksInfo = unitAllocs.map(alloc => {
            const t = flatTasks.find(item => item.id === alloc.taskId);
            const resp = responsibles.find(r => r.id === alloc.responsibleId);
            
            let statusText = 'Planificada';
            let statusColor = 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300';

            if (alloc.executed === true || (alloc.percentCumplimiento !== undefined && alloc.percentCumplimiento >= 100)) {
                statusText = 'Ejecutado (100%)';
                statusColor = 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
            } else if (alloc.executed === false) {
                statusText = 'No Ejecutado';
                statusColor = 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/40 dark:text-red-300';
            } else if (alloc.percentCumplimiento !== undefined && alloc.percentCumplimiento > 0) {
                statusText = `Avance: ${alloc.percentCumplimiento}%`;
                statusColor = 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
            }

            return {
                task: t || { id: alloc.taskId, name: alloc.additionalTaskName || 'Partida Programada', type: 'task' as const },
                subcontractorName: resp ? resp.name : 'Sin Subcontrato asignado',
                percentComprometido: alloc.percentComprometido,
                percentCumplimiento: alloc.percentCumplimiento,
                executed: alloc.executed,
                statusText,
                statusColor
            };
        });

        // Coordenadas para popover
        let popoverX = 300;
        let popoverY = 150;
        if (mouseEvent && mouseEvent.clientX) {
            popoverX = Math.min(window.innerWidth - 380, mouseEvent.clientX + 15);
            popoverY = Math.min(window.innerHeight - 300, mouseEvent.clientY + 15);
        }

        setPopoverInfo({
            unit,
            level,
            x: popoverX,
            y: popoverY,
            tasks: tasksInfo
        });
    };

    // Exportación a PDF de alta resolución con Membrete y Leyenda
    // Optimizado para evitar congelamiento del hilo principal (thread yielding)
    const handleExportPdf = async () => {
        if (!konvaViewerRef.current) return;
        setIsExportingPdf(true);

        // Pausa que libera el hilo principal para que el navegador procese eventos de UI
        const yieldThread = () => new Promise<void>(resolve => {
            requestAnimationFrame(() => setTimeout(resolve, 30));
        });

        try {
            // Fase 1: Captura asíncrona del plano (con pausas internas)
            const dataUrl = await konvaViewerRef.current.toDataURLAsync();
            if (!dataUrl) {
                alert("No se pudo generar la captura del plano.");
                return;
            }

            await yieldThread();

            // Fase 2: Crear documento jsPDF y calcular dimensiones
            const dims = konvaViewerRef.current.getPdfDimensions();
            const isLandscape = dims.width >= dims.height;

            const doc = new jsPDF({
                orientation: isLandscape ? 'landscape' : 'portrait',
                unit: 'mm',
                format: 'a3'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            await yieldThread();

            // Fase 3: Membrete Superior
            doc.setFillColor(30, 41, 59); // slate-800
            doc.rect(0, 0, pageWidth, 22, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(`AVANCE GRÁFICO DE PLANIFICACIÓN — ${projectInfo.name.toUpperCase()}`, 12, 11);

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            const levelLabel = selectedLevelId === 'all' ? 'Todos los Niveles' : (safeLevels.find(l => l.id === selectedLevelId)?.name || 'Nivel');
            const planLabel = activePlan ? activePlan.nombre_visible : 'Plano General';
            const weekLabel = currentActivePlan ? `Semana #${currentActivePlan.id} (${currentActivePlan.startDate} al ${currentActivePlan.endDate})` : 'Planificación';
            doc.text(`${weekLabel}  |  Nivel: ${levelLabel}  |  Plano: ${planLabel}`, 12, 17);

            // Fecha de emisión
            const fechaStr = new Date().toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            doc.text(`Emitido: ${fechaStr}`, pageWidth - 12, 14, { align: 'right' });

            await yieldThread();

            // Fase 4: Layout y cálculo de escalado de imagen
            const margin = 6;
            const topMargin = 24;
            const legendWidth = 72;

            const availableWidth = pageWidth - (margin * 2) - legendWidth - 4;
            const availableHeight = pageHeight - topMargin - margin;

            // Obtener dimensiones reales de la imagen capturada para calcular el escalado óptimo
            const imgProps = doc.getImageProperties(dataUrl);
            const actualImgW = imgProps.width || dims.width || 1;
            const actualImgH = imgProps.height || dims.height || 1;
            const imgAspect = actualImgW / actualImgH;

            // Calcular escalado preservando aspect ratio para ocupar todo el espacio disponible
            let imgW = availableWidth;
            let imgH = imgW / imgAspect;

            if (imgH > availableHeight) {
                imgH = availableHeight;
                imgW = imgH * imgAspect;
            }

            const imgX = margin + (availableWidth - imgW) / 2;
            const imgY = topMargin + (availableHeight - imgH) / 2;

            await yieldThread();

            // Fase 5: Marco e imagen del plano
            doc.setDrawColor(203, 213, 225);
            doc.rect(imgX - 0.5, imgY - 0.5, imgW + 1, imgH + 1, 'S');

            // Usar JPEG para addImage ya que toDataURLAsync genera JPEG
            doc.addImage(dataUrl, 'JPEG', imgX, imgY, imgW, imgH);

            await yieldThread();

            // Fase 6: Tabla de Leyenda Lateral en PDF (autoTable)
            const legendData = weeklyProgrammedTasks.map(pt => [
                '', // Espacio para el indicador de color/símbolo
                pt.task.name,
                pt.isSurface ? 'Área' : 'Puntual',
                `${pt.unitIds.size}`
            ]);

            if (legendData.length > 0) {
                // Posición de la Leyenda
                const legendX = pageWidth - legendWidth - margin;
                
                // Calculamos altura estimada para la leyenda según filas
                const estimatedRowsH = Math.min(availableHeight - 12, legendData.length * 6.5 + 20);

                doc.setFillColor(248, 250, 252);
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.roundedRect(legendX, topMargin, legendWidth, estimatedRowsH, 2, 2, 'FD');

                // Título de la tarjeta de leyenda
                doc.setFillColor(30, 41, 59);
                doc.roundedRect(legendX, topMargin, legendWidth, 8, 2, 2, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'bold');
                doc.text('LEYENDA DE PLANIFICACIÓN', legendX + legendWidth / 2, topMargin + 5.5, { align: 'center' });

                await yieldThread();

                autoTable(doc, {
                    startY: topMargin + 9,
                    margin: { left: legendX + 1.5, right: margin + 1.5 },
                    tableWidth: legendWidth - 3,
                    head: [['', 'Partida Programada', 'Tipo', 'Cant.']],
                    body: legendData,
                    theme: 'striped',
                    headStyles: {
                        fillColor: [51, 65, 85],
                        textColor: [255, 255, 255],
                        fontSize: 7.5,
                        fontStyle: 'bold',
                        halign: 'center',
                        cellPadding: 1.8
                    },
                    bodyStyles: {
                        fontSize: 7,
                        textColor: [30, 41, 59],
                        valign: 'middle',
                        cellPadding: 1.8,
                        overflow: 'ellipsize'
                    },
                    alternateRowStyles: {
                        fillColor: [241, 245, 249]
                    },
                    columnStyles: {
                        0: { cellWidth: 6, halign: 'center' },
                        1: { cellWidth: 41, fontStyle: 'bold' },
                        2: { cellWidth: 12, halign: 'center', fontSize: 6.5 },
                        3: { cellWidth: 10, halign: 'center', fontStyle: 'bold' }
                    },
                    styles: {
                        lineColor: [226, 232, 240],
                        lineWidth: 0.2
                    },
                    didDrawCell: (data) => {
                        if (data.section === 'body' && data.column.index === 0) {
                            const taskItem = weeklyProgrammedTasks[data.row.index];
                            if (taskItem) {
                                const hex = taskItem.color || '#3B82F6';
                                const r = parseInt(hex.slice(1, 3), 16) || 59;
                                const g = parseInt(hex.slice(3, 5), 16) || 130;
                                const b = parseInt(hex.slice(5, 7), 16) || 246;

                                const cx = data.cell.x + data.cell.width / 2;
                                const cy = data.cell.y + data.cell.height / 2;

                                doc.setFillColor(r, g, b);
                                doc.setDrawColor(255, 255, 255);
                                doc.setLineWidth(0.3);

                                if (taskItem.isSurface) {
                                    // Rectángulo representativo de superficie
                                    doc.rect(cx - 2.2, cy - 2.2, 4.4, 4.4, 'FD');
                                } else {
                                    // Símbolo puntual
                                    if (taskItem.shape === 'circle') {
                                        doc.circle(cx, cy, 2.2, 'FD');
                                    } else if (taskItem.shape === 'diamond') {
                                        doc.lines([[2.2, 2.2], [-2.2, 2.2], [-2.2, -2.2], [2.2, -2.2]], cx, cy - 2.2, [1, 1], 'FD', true);
                                    } else {
                                        doc.roundedRect(cx - 2.2, cy - 2.2, 4.4, 4.4, 0.6, 0.6, 'FD');
                                    }
                                }
                            }
                        }
                    }
                });
            }

            await yieldThread();

            // Fase 7: Pie de página y descarga
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text(`K-LyDApp / ConstructOS — Sistema de Gestión y Control de Producción`, margin, pageHeight - 4);

            doc.save(`Avance_Grafico_Planificacion_${projectInfo.name.replace(/\s+/g, '_')}_Semana_${currentActivePlan?.id || 1}.pdf`);
        } catch (err) {
            console.error("Error exportando PDF de avance gráfico:", err);
            alert("Ocurrió un error al generar el archivo PDF.");
        } finally {
            setIsExportingPdf(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center h-full">
                <span className="material-symbols-outlined animate-spin text-4xl text-blue-500">sync</span>
            </div>
        );
    }

    if (plans.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[550px] bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600 mb-4">map</span>
                <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">Sin Planos Configurados</h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-md">
                    Este proyecto aún no tiene planos cargados. Dirígete a E.D.T y selecciona "Configuración de Avance Gráfico" para subir planos y mapear los recintos.
                </p>
                {onClose && (
                    <button onClick={onClose} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Cerrar Visor
                    </button>
                )}
            </div>
        );
    }

    return (
        <div 
            ref={containerRef} 
            className="flex flex-col h-full w-full bg-slate-100 dark:bg-slate-950 overflow-hidden relative select-none rounded-xl"
        >
            {/* Barra Superior Flotante con Filtros y Botones de Acción */}
            <div className="absolute top-3 left-3 right-3 z-20 flex items-start justify-between pointer-events-none gap-3">
                {/* Controles de Nivel y Plano */}
                <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-2 pointer-events-auto flex items-center gap-2.5 flex-wrap">
                    {/* Filtro por Nivel */}
                    <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-blue-600 text-[18px]">layers</span>
                        <span className="font-extrabold text-slate-800 dark:text-white text-xs uppercase tracking-wide">Nivel:</span>
                        <select
                            value={selectedLevelId}
                            onChange={e => setSelectedLevelId(e.target.value)}
                            className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-white font-medium outline-none focus:ring-1 focus:ring-blue-500"
                        >
                            <option value="all">Todos los Niveles</option>
                            {safeLevels.map(lvl => (
                                <option key={lvl.id} value={lvl.id}>{lvl.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>

                    {/* Selector de Plano */}
                    <div className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-blue-600 text-[18px]">map</span>
                        <span className="font-extrabold text-slate-800 dark:text-white text-xs uppercase tracking-wide">Plano:</span>
                        <select
                            value={selectedPlanId}
                            onChange={e => setSelectedPlanId(e.target.value)}
                            title={plans.find(p => p.id === selectedPlanId)?.nombre_visible}
                            className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-white font-medium outline-none focus:ring-1 focus:ring-blue-500 max-w-[200px] truncate"
                        >
                            {plans.map(p => {
                                const revMatch = p.nombre_original?.match(/\|rev:(\d+)$/);
                                const rev = revMatch ? ` (Rev. ${revMatch[1]})` : ' (Rev. 1)';
                                return (
                                    <option key={p.id} value={p.id} title={p.nombre_visible}>
                                        {p.nombre_visible}{rev}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>

                    {/* Botón Descargar PDF */}
                    <button
                        onClick={handleExportPdf}
                        disabled={isExportingPdf || !activePlan}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                        title="Descargar plano en formato PDF con membrete y leyenda"
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {isExportingPdf ? 'sync' : 'picture_as_pdf'}
                        </span>
                        <span>{isExportingPdf ? 'Generando...' : 'Descargar PDF'}</span>
                    </button>

                    {/* Botón Pantalla Completa / Ampliar Vista */}
                    <button
                        onClick={handleToggleFullscreen}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                        title={isFullscreen ? "Restaurar tamaño normal" : "Ampliar visor a pantalla completa"}
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                        </span>
                        <span>{isFullscreen ? 'Salir' : 'Agrandar Pantalla'}</span>
                    </button>
                </div>
            </div>

            {/* Canvas y Leyenda Lateral */}
            <div className="flex-1 w-full h-full flex overflow-hidden relative">
                {/* Visor de Plano Konva */}
                <div className="flex-1 w-full h-full">
                    {activePlan ? (
                        <PdfKonvaViewer
                            ref={konvaViewerRef}
                            pdfUrl={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/graphic_plans/${activePlan.ruta_storage}`}
                            regions={konvaRegions}
                            interactive={false}
                            selectedRegionId={popoverInfo?.unit?.id}
                            onRegionClick={(reg: any) => handleRegionSelect(reg)}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-400 font-medium">
                            Selecciona un plano para visualizar
                        </div>
                    )}
                </div>

                {/* Leyenda Dinámica Lateral (Partidas Programadas de la Semana) */}
                <div className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-2xl flex flex-col z-30 shrink-0">
                    <div className="p-3.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/80 flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase font-black tracking-wider text-blue-600 dark:text-blue-400">
                                Leyenda de Planificación
                            </div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">
                                Partidas de la Semana
                            </h4>
                        </div>
                        <span className="text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 px-2 py-0.5 rounded-full">
                            {weeklyProgrammedTasks.length} {weeklyProgrammedTasks.length === 1 ? 'partida' : 'partidas'}
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                        {weeklyProgrammedTasks.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-xs italic">
                                No hay partidas programadas para esta semana en el nivel seleccionado.
                            </div>
                        ) : (
                            weeklyProgrammedTasks.map(({ task, color, isSurface, shape, unitIds }) => (
                                <div
                                    key={task.id}
                                    className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 shadow-sm flex flex-col gap-1.5 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        {/* Muestra gráfica de la leyenda (Superficie vs Puntual) */}
                                        {isSurface ? (
                                            <div
                                                className="w-4 h-4 rounded border shadow-sm shrink-0"
                                                style={{ backgroundColor: color, borderColor: color }}
                                                title="Partida de Superficie (Relleno / Franja)"
                                            />
                                        ) : (
                                            <div
                                                className="w-4 h-4 flex items-center justify-center shadow-sm shrink-0 border border-slate-300 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-800"
                                                title={`Partida Puntual (${shape})`}
                                            >
                                                <div
                                                    className="w-2.5 h-2.5"
                                                    style={{
                                                        backgroundColor: color,
                                                        borderRadius: shape === 'circle' ? '50%' : shape === 'diamond' ? '0' : '2px',
                                                        transform: shape === 'diamond' ? 'rotate(45deg)' : 'none'
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 leading-tight truncate flex-1" title={task.name}>
                                            {task.name}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between text-[10px] text-slate-500 font-semibold px-0.5">
                                        <span className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[12px]">
                                                {isSurface ? 'square_foot' : 'pin_drop'}
                                            </span>
                                            {isSurface ? 'Superficie (m²)' : `Puntual (${task.unit || 'un'})`}
                                        </span>
                                        <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300 font-bold">
                                            {unitIds.size} {unitIds.size === 1 ? 'recinto' : 'recintos'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Resumen de Tipos de Renderizado */}
                    <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 text-[11px] text-slate-500 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-blue-500/40 border border-blue-500"></div>
                            <span><strong>Franjas / Área:</strong> Partidas en $m^2$</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500 border border-white"></div>
                            <span><strong>Símbolos:</strong> Partidas en $un$, $pza$, $m$</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Popover Informativo Flotante por Unidad Física */}
            {popoverInfo && (
                <div
                    className="fixed z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 p-3.5 w-80 animate-in fade-in zoom-in-95 duration-150"
                    style={{ left: `${popoverInfo.x}px`, top: `${popoverInfo.y}px` }}
                >
                    <div className="flex items-start justify-between gap-2 border-b border-slate-200 dark:border-slate-700 pb-2 mb-2.5">
                        <div>
                            <div className="text-[10px] uppercase font-black tracking-wider text-blue-600 dark:text-blue-400">
                                {popoverInfo.level ? popoverInfo.level.name : 'Recinto'}
                            </div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">
                                {popoverInfo.unit.name}
                            </h4>
                        </div>
                        <button
                            onClick={() => setPopoverInfo(null)}
                            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {popoverInfo.tasks.length === 0 ? (
                            <p className="text-xs text-slate-400 italic text-center py-2">
                                Sin partidas programadas en esta semana para este recinto.
                            </p>
                        ) : (
                            popoverInfo.tasks.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 flex flex-col gap-1 text-xs"
                                >
                                    <div className="flex items-start justify-between gap-1.5">
                                        <span className="font-bold text-slate-800 dark:text-slate-200 leading-snug">
                                            {item.task.name}
                                        </span>
                                        <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded border uppercase shrink-0 ${item.statusColor}`}>
                                            {item.statusText}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-[10px] text-slate-500">
                                        <span>Subcontrato:</span>
                                        <span className="font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[140px]" title={item.subcontractorName}>
                                            {item.subcontractorName}
                                        </span>
                                    </div>
                                    {item.percentComprometido !== undefined && (
                                        <div className="flex items-center justify-between text-[10px] text-slate-500">
                                            <span>% Comprometido:</span>
                                            <span className="font-bold text-blue-600 dark:text-blue-400">{item.percentComprometido}%</span>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
